'use client'

import { ArrowRight } from 'lucide-react'
import Link from 'next/link'

import { AuthCard, AuthEyebrow } from '@/components/molecules/auth-card'
import { NextStepsNote } from '@/components/sections/creator/next-steps-note'
import { BODY } from '@/lib/typography'
import { cn } from '@/lib/utils'

/**
 * No account, no order — and the reason is said here rather than discovered
 * after the form is full.
 *
 * An order in FLOU is a commitment to pay up front (spec §1) and paying needs
 * an account (spec §3), so the wall belongs at the order rather than at a
 * payment screen that would strand somebody who had already typed everything.
 *
 * The basket is not lost by leaving: it is a row addressed by a cookie that
 * signing up does not touch, and it becomes the new account's on the first
 * thing they do afterwards. The copy promises exactly that, because a person
 * who does not believe it will not go.
 */
export function OrderSignInCard({
  id,
  locale,
  className,
}: {
  id?: string
  locale: string
  className?: string
}) {
  return (
    <AuthCard id={id} className={cn('scroll-mt-28 flex flex-col gap-5', className)}>
      <AuthEyebrow>Dane do zamówienia</AuthEyebrow>
      <div>
        <h2 className="text-[1.5rem] font-bold tracking-tight text-white sm:text-[1.7rem]">
          Zaloguj się, żeby zamówić
        </h2>
        <p className={cn(BODY, 'mt-2 text-white/60')}>
          Zamówienie w FLOU jest płatne z góry, więc potrzebujemy konta — na nim widzisz status,
          pliki i faktury. <span className="text-white/80">Koszyk na Ciebie poczeka</span>: wróć tu
          po zalogowaniu, a wszystko będzie na miejscu.
        </p>
      </div>
      <div className="flex flex-col gap-3">
        <Link
          href={`/${locale}/register`}
          className={cn(
            BODY,
            'inline-flex items-center justify-center gap-2 rounded-full bg-white py-3.5 font-semibold text-neutral-900 transition-colors duration-200 hover:bg-white/90',
          )}
        >
          Załóż konto
          <ArrowRight size={14} aria-hidden="true" />
        </Link>
        <Link
          href={`/${locale}/login`}
          className={cn(
            BODY,
            'inline-flex items-center justify-center rounded-full border border-white/20 py-3.5 font-semibold text-white/80 transition-colors duration-200 hover:border-white/45 hover:text-white',
          )}
        >
          Mam już konto
        </Link>
      </div>
      <NextStepsNote />
    </AuthCard>
  )
}
